import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    FaArrowLeft,
    FaChevronLeft,
    FaChevronRight,
    FaDownload,
    FaEnvelope,
    FaFileExcel,
    FaFileUpload,
    FaPaperclip,
    FaSearch,
    FaSyncAlt,
} from 'react-icons/fa'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api, { apiUpload } from '../../../helpers/apiRequester'
import { ClaimDetailDrawer, ClaimDrawerTarget } from '../omnibusShared/claimDetailDrawer'
import { CollapsibleSection } from '../omnibusShared/collapsibleSection'
import { CommunicationsPanel } from '../omnibusShared/communicationsPanel'
import { formatClientName, formatCount, formatDate, formatMoney, formatMoneyCompact } from '../omnibusShared/formatting'
import { useIsAdmin } from '../omnibusShared/useIsAdmin'
import styles from './styles.module.scss'

interface OmnibusGroup {
    id: number
    omnibus_ref: string
    defendant: string
    sequence_no: number
    status: string
    notes: string | null
    created_at: string | null
    updated_at: string | null
}

interface OmnibusGroupStats {
    total_claims: number
    total_clients: number
    claims_captured: number
    claims_awaiting_capture: number
    total_documents: number
    total_quantum: number | null
    avg_quantum: number | null
    max_quantum: number | null
    min_quantum: number | null
    total_loan_value: number | null
    avg_loan_value: number | null
    max_loan_value: number | null
    min_loan_value: number | null
    total_ate_premium: number | null
    total_monthly_repayment: number | null
    earliest_agreement: string | null
    latest_agreement: string | null
    first_joined_at: string | null
    last_joined_at: string | null
    clients_with_multiple_claims: number
    max_claims_per_client: number
}

interface OmnibusMilestone {
    key: string
    label: string
    order: number
    claim_count: number
}

interface OmnibusSchedule {
    id: number
    schedule_number: number
    status: string
    sent_at: string | null
    sent_by: string | null
    claim_count: number
    email_to: string | null
    email_subject: string | null
    file_name: string | null
    generated_at: string | null
    has_file: boolean
    created_at: string | null
}

interface MasterSchedule {
    kind: 'master'
    file_name: string | null
    claim_count: number
    generated_at: string | null
    has_file: boolean
}

interface IntroducerBreakdown {
    introducer: string
    claim_count: number
    client_count: number
    total_quantum: number | null
}

interface BatchBreakdown {
    batch_group: string
    claim_count: number
    total_quantum: number | null
}

interface OmnibusOverview {
    group: OmnibusGroup
    stats: OmnibusGroupStats
    milestones: OmnibusMilestone[]
    schedules: OmnibusSchedule[]
    master_schedule: MasterSchedule | null
    claims_awaiting_schedule: number
    /** Sentinel admins only — omitted from the response for everyone else. */
    viewer_is_admin?: boolean
    by_introducer?: IntroducerBreakdown[]
    by_batch: BatchBreakdown[]
}

type EmailUploadStatus = 'imported' | 'duplicate' | 'failed'

interface EmailUploadFile {
    filename: string
    status: EmailUploadStatus
    message?: string | null
    defendants?: string[]
}

interface EmailUploadSummary {
    imported: number
    duplicates: number
    failed: number
    files: EmailUploadFile[]
}

interface OmnibusGroupClaim {
    id: number
    funder_case_ref: string
    funder_client_id: number
    joined_at: string | null
    first_name: string | null
    last_name: string | null
    email_address: string | null
    agreement_number: string | null
    ate_policy_number: string | null
    quantum_individual: number | string | null
    loan_value: number | string | null
    monthly_repayment: number | string | null
    contract_term: string | null
    agreement_start_date: string | null
    introducer: string | null
    batch_group: string | null
    client_status: string | null
    enrichment_status: string | null
    document_count: number
    comms_count: number
    has_detail: boolean
    milestone: string
}

const PAGE_SIZE = 50

const SORT_OPTIONS = [
    { value: 'joined', label: 'Recently added' },
    { value: 'quantum', label: 'Highest quantum' },
    { value: 'loan', label: 'Highest loan' },
    { value: 'client', label: 'Client name' },
]

export const OmnibusGroupDetailView = () => {
    const { omnibusId } = useParams<{ omnibusId: string }>()
    const navigate = useNavigate()
    // Document counts/links are Sentinel-admin only; this page is for
    // monitoring the omnibus, not reviewing paperwork.
    const isAdmin = useIsAdmin()

    const [overview, setOverview] = useState<OmnibusOverview | null>(null)
    const [overviewLoading, setOverviewLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [claims, setClaims] = useState<OmnibusGroupClaim[]>([])
    const [claimsLoading, setClaimsLoading] = useState(false)
    const [total, setTotal] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [page, setPage] = useState(1)

    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [milestone, setMilestone] = useState('')
    const [sort, setSort] = useState('joined')
    const [commsFilter, setCommsFilter] = useState('')
    const [commsCoverage, setCommsCoverage] = useState<{
        total_claims: number
        claims_with_comms: number
        claims_without_comms: number
    } | null>(null)

    const [detailClaim, setDetailClaim] = useState<ClaimDrawerTarget | null>(null)
    const [capturing, setCapturing] = useState(false)
    const [buildingMaster, setBuildingMaster] = useState(false)
    const [buildingNext, setBuildingNext] = useState(false)
    const [markingSentId, setMarkingSentId] = useState<number | null>(null)
    const [downloadingKey, setDownloadingKey] = useState<string | null>(null)

    const [emailUploading, setEmailUploading] = useState(false)
    const [emailSummary, setEmailSummary] = useState<EmailUploadSummary | null>(null)
    const [communicationsKey, setCommunicationsKey] = useState(0)
    const emailInputRef = useRef<HTMLInputElement>(null)

    const fetchOverview = useCallback(async () => {
        if (!omnibusId) return
        setOverviewLoading(true)
        setError(null)
        try {
            const response = await api.post('omnibus-group-overview', {
                omnibus_id: Number(omnibusId),
            })
            if (!response.data?.success) {
                const message = response.data?.message || 'Could not load this omnibus.'
                setError(message)
                return
            }
            setOverview(response.data as OmnibusOverview)
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to load this omnibus.'
            setError(message)
        } finally {
            setOverviewLoading(false)
        }
    }, [omnibusId])

    const fetchClaims = useCallback(async () => {
        if (!omnibusId) return
        setClaimsLoading(true)
        try {
            const response = await api.post('omnibus-group-claims', {
                omnibus_id: Number(omnibusId),
                search,
                milestone,
                sort,
                comms: commsFilter,
                page,
                page_size: PAGE_SIZE,
            })
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not load claims.', 'Okay')
                return
            }
            setClaims(response.data.claims || [])
            setTotal(response.data.total || 0)
            setTotalPages(response.data.total_pages || 1)
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || err?.message || 'Failed to load claims.', 'Okay')
        } finally {
            setClaimsLoading(false)
        }
    }, [omnibusId, search, milestone, sort, commsFilter, page])

    useEffect(() => {
        fetchOverview()
    }, [fetchOverview])

    useEffect(() => {
        if (!omnibusId) return
        api.post('omnibus-client-comms-coverage', { omnibus_id: Number(omnibusId), page_size: 1 })
            .then((response) => {
                const row = response.data?.per_omnibus?.[0]
                if (row) {
                    setCommsCoverage({
                        total_claims: row.total_claims,
                        claims_with_comms: row.claims_with_comms,
                        claims_without_comms: row.claims_without_comms,
                    })
                }
            })
            .catch(() => setCommsCoverage(null))
    }, [omnibusId])

    useEffect(() => {
        fetchClaims()
    }, [fetchClaims])

    // Debounce typing so each keystroke doesn't hit the API.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim())
            setPage(1)
        }, 350)
        return () => clearTimeout(timer)
    }, [searchInput])

    const statTiles = useMemo(() => {
        if (!overview) return []
        const s = overview.stats
        return [
            { label: 'Total claims', value: formatCount(s.total_claims) },
            { label: 'Total clients', value: formatCount(s.total_clients) },
            { label: 'Omnibus value', value: formatMoneyCompact(s.total_quantum), tone: 'accent' },
            { label: 'Average quantum', value: formatMoney(s.avg_quantum) },
            { label: 'Highest quantum', value: formatMoney(s.max_quantum) },
            { label: 'Lowest quantum', value: formatMoney(s.min_quantum) },
            { label: 'Total loan value', value: formatMoneyCompact(s.total_loan_value) },
            { label: 'Highest loan', value: formatMoney(s.max_loan_value) },
            { label: 'Lowest loan', value: formatMoney(s.min_loan_value) },
            { label: 'Total ATE premium', value: formatMoneyCompact(s.total_ate_premium) },
            ...(isAdmin ? [{ label: 'Documents held', value: formatCount(s.total_documents) }] : []),
            {
                label: 'Clients with 2+ claims',
                value: formatCount(s.clients_with_multiple_claims),
            },
        ]
    }, [overview, isAdmin])

    const captureProgress = useMemo(() => {
        if (!overview) return 0
        const { claims_captured: captured, total_claims: totalClaims } = overview.stats
        if (!totalClaims) return 0
        return Math.round((captured / totalClaims) * 100)
    }, [overview])

    const workingSchedule = useMemo(() => overview?.schedules.find((schedule) => !schedule.sent_at) || null, [overview])

    const runCapture = useCallback(async () => {
        if (!omnibusId) return
        setCapturing(true)
        try {
            const response = await api.post('omnibus-capture-group', {
                omnibus_id: Number(omnibusId),
            })
            if (!response.data?.success) {
                throw new Error(response.data?.message || 'Capture failed')
            }
            const result = response.data.result
            Report.success(
                'Capture complete',
                `${formatCount(result.details_written)} claim(s) captured, ${formatCount(
                    result.documents_written,
                )} document link(s) stored.${
                    result.remaining > 0
                        ? ` ${formatCount(result.remaining)} still outstanding — run again to continue.`
                        : ''
                }`,
                'Close',
            )
            await Promise.all([fetchOverview(), fetchClaims()])
        } catch (err: any) {
            Report.failure('Capture failed', err?.response?.data?.message || err?.message || 'Unknown error', 'Close')
        } finally {
            setCapturing(false)
        }
    }, [omnibusId, fetchOverview, fetchClaims])

    const buildMasterSchedule = useCallback(async () => {
        if (!omnibusId) return
        setBuildingMaster(true)
        try {
            const response = await api.post('omnibus-build-master-schedule', {
                omnibus_id: Number(omnibusId),
            })
            if (!response.data?.success) {
                throw new Error(response.data?.message || 'Could not build master schedule')
            }
            const master = response.data.master
            Report.success(
                'Master schedule ready',
                `${formatCount(master.claim_count)} claim(s) written to ${master.file_name}.${
                    master.claims_missing_detail > 0
                        ? ` ${formatCount(master.claims_missing_detail)} still missing captured detail.`
                        : ''
                }`,
                'Close',
            )
            await fetchOverview()
        } catch (err: any) {
            Report.failure(
                'Master schedule failed',
                err?.response?.data?.message || err?.message || 'Unknown error',
                'Close',
            )
        } finally {
            setBuildingMaster(false)
        }
    }, [omnibusId, fetchOverview])

    const buildNextSchedule = useCallback(async () => {
        if (!omnibusId) return
        setBuildingNext(true)
        try {
            const response = await api.post('omnibus-build-next-schedule', {
                omnibus_id: Number(omnibusId),
            })
            if (!response.data?.success) {
                throw new Error(response.data?.message || 'Could not build schedule')
            }
            const schedule = response.data.schedule
            Report.success(
                schedule.action === 'updated'
                    ? `Schedule #${schedule.schedule_number} updated`
                    : `Schedule #${schedule.schedule_number} ready`,
                schedule.action === 'updated'
                    ? `${formatCount(schedule.added_claim_count)} new claim(s) appended. The working sheet now contains ${formatCount(schedule.claim_count)} claim(s).`
                    : `${formatCount(schedule.claim_count)} claim(s) written to ${schedule.file_name}. The master list was refreshed at the same time.`,
                'Close',
            )
            await fetchOverview()
        } catch (err: any) {
            Report.failure(
                'Schedule build failed',
                err?.response?.data?.message || err?.message || 'Unknown error',
                'Close',
            )
        } finally {
            setBuildingNext(false)
        }
    }, [omnibusId, fetchOverview])

    const downloadSchedule = useCallback(
        async (kind: 'master' | 'incremental', scheduleId?: number) => {
            if (!omnibusId) return
            const key = kind === 'master' ? 'master' : `schedule-${scheduleId}`
            setDownloadingKey(key)
            try {
                const response = await api.post('omnibus-schedule-download', {
                    omnibus_id: Number(omnibusId),
                    kind,
                    schedule_id: scheduleId,
                })
                if (!response.data?.success || !response.data.download_url) {
                    throw new Error(response.data?.message || 'Download unavailable')
                }
                window.open(response.data.download_url, '_blank', 'noopener,noreferrer')
            } catch (err: any) {
                Report.failure(
                    'Download failed',
                    err?.response?.data?.message || err?.message || 'Unknown error',
                    'Close',
                )
            } finally {
                setDownloadingKey(null)
            }
        },
        [omnibusId],
    )

    const markScheduleSent = useCallback(
        async (scheduleId: number) => {
            if (!omnibusId) return
            setMarkingSentId(scheduleId)
            try {
                const response = await api.post('omnibus-mark-schedule-sent', {
                    omnibus_id: Number(omnibusId),
                    schedule_id: scheduleId,
                })
                if (!response.data?.success) {
                    throw new Error(response.data?.message || 'Could not mark schedule as sent')
                }
                Report.success('Marked as sent', 'Claims on this schedule now count toward milestone 1.', 'Close')
                await fetchOverview()
            } catch (err: any) {
                Report.failure(
                    'Could not mark sent',
                    err?.response?.data?.message || err?.message || 'Unknown error',
                    'Close',
                )
            } finally {
                setMarkingSentId(null)
            }
        },
        [omnibusId, fetchOverview],
    )

    const pickEmailFiles = useCallback(() => emailInputRef.current?.click(), [])

    const uploadEmailFiles = useCallback(async (files: File[]) => {
        if (!files.length || !omnibusId) return
        setEmailUploading(true)
        try {
            const formData = new FormData()
            files.forEach((file) => formData.append('emails', file))
            // Uploading from a lender's page attaches the thread to that lender,
            // so the file name and subject line do not have to match it.
            formData.append('omnibus_id', String(omnibusId))
            const response = await apiUpload('omnibus-communications-import', formData)
            if (response.data?.success === false) {
                throw new Error(response.data?.message || 'Could not upload these emails.')
            }
            const rows: EmailUploadFile[] = (response.data?.files || []).map((row: any) => ({
                filename: row.filename,
                status: row.status,
                message: row.message ?? null,
                defendants: row.defendants || [],
            }))
            const summary: EmailUploadSummary = {
                imported: Number(response.data?.imported) || 0,
                duplicates: Number(response.data?.duplicates) || 0,
                failed: Number(response.data?.failed) || 0,
                files: rows,
            }
            setEmailSummary(summary)
            setCommunicationsKey((key) => key + 1)
            fetchOverview()
            Report.success(
                'Emails uploaded',
                `${formatCount(summary.imported)} attached, ${formatCount(summary.duplicates)} already held, ${formatCount(summary.failed)} failed.`,
                'Close',
            )
        } catch (err: any) {
            Report.failure('Upload failed', err?.response?.data?.message || err?.message || 'Unknown error', 'Close')
        } finally {
            setEmailUploading(false)
        }
    }, [omnibusId, fetchOverview])

    const openClaim = useCallback((claim: OmnibusGroupClaim) => {
        setDetailClaim({
            id: claim.id,
            funder_case_ref: claim.funder_case_ref,
            funder_client_id: claim.funder_client_id,
            first_name: claim.first_name,
            last_name: claim.last_name,
        })
    }, [])

    if (error) {
        return (
            <div className={styles.wrap}>
                <button type="button" className={styles.backLink} onClick={() => navigate('/omnibus-claims')}>
                    <FaArrowLeft /> Back to omnibus groups
                </button>
                <div className={styles.errorBanner}>{error}</div>
            </div>
        )
    }

    return (
        <div className={styles.wrap}>
            <button type="button" className={styles.backLink} onClick={() => navigate('/omnibus-claims')}>
                <FaArrowLeft /> Back to omnibus groups
            </button>

            <input
                ref={emailInputRef}
                className={styles.hiddenFileInput}
                type="file"
                accept=".eml,message/rfc822"
                multiple
                onChange={(event) => {
                    const chosen = Array.from(event.target.files || []).filter((file) =>
                        file.name.toLowerCase().endsWith('.eml'),
                    )
                    event.target.value = ''
                    uploadEmailFiles(chosen)
                }}
            />

            {overviewLoading && !overview && <div className={styles.emptyState}>Loading omnibus…</div>}

            {overview && (
                <>
                    <header className={styles.pageHeader}>
                        <div>
                            <h2 className={styles.lenderName}>{overview.group.defendant}</h2>
                            <div className={styles.headerMeta}>
                                <span className={styles.refCode}>{overview.group.omnibus_ref}</span>
                                <span className={`${styles.statusPill} ${styles[overview.group.status] || ''}`}>
                                    {overview.group.status}
                                </span>
                                <span className={styles.metaText}>Created {formatDate(overview.group.created_at)}</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.refreshBtn}
                            onClick={() => {
                                fetchOverview()
                                fetchClaims()
                            }}
                            disabled={overviewLoading || claimsLoading}
                        >
                            <FaSyncAlt /> Refresh
                        </button>
                    </header>

                    <CollapsibleSection title="Key figures" count={`${statTiles.length} measures`}>
                        <div className={styles.statGridInline}>
                            {statTiles.map((tile) => (
                                <div
                                    key={tile.label}
                                    className={`${styles.statTile} ${tile.tone ? styles[tile.tone] : ''}`}
                                >
                                    <span className={styles.statValue}>{tile.value}</span>
                                    <span className={styles.statLabel}>{tile.label}</span>
                                </div>
                            ))}
                        </div>
                    </CollapsibleSection>

                    {overview.stats.claims_awaiting_capture > 0 && (
                        <div className={styles.captureBanner}>
                            <div>
                                <strong>
                                    {formatCount(overview.stats.claims_captured)} of{' '}
                                    {formatCount(overview.stats.total_claims)}
                                </strong>{' '}
                                claims have their full record captured ({captureProgress}%). Values above only count
                                captured claims, so they will rise as the remaining{' '}
                                {formatCount(overview.stats.claims_awaiting_capture)} are imported.
                            </div>
                            <div className={styles.progressTrack}>
                                <div className={styles.progressFill} style={{ width: `${captureProgress}%` }} />
                            </div>
                        </div>
                    )}

                    <div className={`${styles.columns} ${isAdmin ? '' : styles.columnsSingle}`}>
                        <CollapsibleSection
                            title="Journey progress"
                            hint="Opening a defendant email thread is the first step toward requesting disclosure on the coming schedule. Claims then move from awaiting a schedule to sent."
                        >
                            <ul className={styles.milestoneList}>
                                {overview.milestones.map((m) => {
                                    const pct = overview.stats.total_claims
                                        ? Math.round((m.claim_count / overview.stats.total_claims) * 100)
                                        : 0
                                    return (
                                        <li key={m.key} className={styles.milestoneItem}>
                                            <button
                                                type="button"
                                                className={`${styles.milestoneBtn} ${
                                                    milestone === m.key ? styles.active : ''
                                                } ${pct === 100 ? styles.complete : ''}`}
                                                onClick={() => {
                                                    setMilestone((c) => (c === m.key ? '' : m.key))
                                                    setPage(1)
                                                }}
                                            >
                                                <span className={styles.milestoneLabel}>{m.label}</span>
                                                <span className={styles.milestoneCount}>
                                                    {formatCount(m.claim_count)}
                                                </span>
                                            </button>
                                            <div className={styles.progressTrack}>
                                                <div className={styles.progressFill} style={{ width: `${pct}%` }} />
                                            </div>
                                        </li>
                                    )
                                })}
                            </ul>
                        </CollapsibleSection>

                        {/* Write actions (build/update schedules, capture, upload emails) are
                            admin-only; monitoring users only see the journey + schedules. The
                            backend re-checks admin on each of these endpoints. */}
                        {isAdmin && (
                            <CollapsibleSection
                                title="Actions"
                                hint="New claims are appended to the current working sheet until it is marked sent. Only after that email has gone out will the next build create a new numbered schedule."
                            >
                            <ul className={styles.actionList}>
                                <li className={styles.actionItem}>
                                    <div>
                                        <span className={styles.actionName}>
                                            {workingSchedule
                                                ? `Update schedule #${workingSchedule.schedule_number}`
                                                : 'Build next schedule sheet'}
                                        </span>
                                        <span className={styles.actionMeta}>
                                            {formatCount(overview.claims_awaiting_schedule)} new claim(s) waiting to be{' '}
                                            {workingSchedule ? 'appended' : 'added'}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.actionBtnPrimary}
                                        onClick={buildNextSchedule}
                                        disabled={
                                            buildingNext || buildingMaster || overview.claims_awaiting_schedule === 0
                                        }
                                    >
                                        {buildingNext
                                            ? 'Building…'
                                            : workingSchedule
                                              ? 'Update schedule'
                                              : 'Build schedule'}
                                    </button>
                                </li>

                                {overview.stats.claims_awaiting_capture > 0 && (
                                    <li className={styles.actionItem}>
                                        <div>
                                            <span className={styles.actionName}>Capture claim data</span>
                                            <span className={styles.actionMeta}>
                                                {formatCount(overview.stats.claims_awaiting_capture)} claim(s) have no
                                                snapshot yet, so quantum, loan and policy details are blank
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.actionBtnPrimary}
                                            onClick={runCapture}
                                            disabled={capturing}
                                        >
                                            {capturing ? 'Capturing…' : 'Capture now'}
                                        </button>
                                    </li>
                                )}

                                <li className={styles.actionItem}>
                                    <div>
                                        <span className={styles.actionName}>Upload defendant emails</span>
                                        <span className={styles.actionMeta}>
                                            Attach the .eml files exported from Outlook. Each chain is matched to its
                                            lender and appears under Defendant communications below.
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.actionBtnPrimary}
                                        onClick={pickEmailFiles}
                                        disabled={emailUploading}
                                    >
                                        <FaFileUpload /> {emailUploading ? 'Uploading…' : 'Choose .eml files'}
                                    </button>
                                </li>
                            </ul>

                            {emailSummary && (
                                <div className={styles.uploadSummary}>
                                    <div className={styles.uploadCounts}>
                                        <span>{formatCount(emailSummary.imported)} attached</span>
                                        <span>{formatCount(emailSummary.duplicates)} already held</span>
                                        <span>{formatCount(emailSummary.failed)} failed</span>
                                    </div>
                                    <ul className={styles.uploadList}>
                                        {emailSummary.files.map((file, index) => (
                                            <li key={`${file.filename}-${index}`}>
                                                <span className={`${styles.uploadStatus} ${styles[file.status]}`}>
                                                    {file.status}
                                                </span>
                                                <span className={styles.uploadFile}>
                                                    <span className={styles.fileName}>{file.filename}</span>
                                                    <span className={styles.actionMeta}>
                                                        {file.message ||
                                                            (file.defendants?.length
                                                                ? `Matched ${file.defendants.join(', ')}`
                                                                : 'No lender matched')}
                                                    </span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            </CollapsibleSection>
                        )}
                    </div>

                    <CommunicationsPanel
                        omnibusId={overview.group.id}
                        defendant={overview.group.defendant}
                        omnibusRef={overview.group.omnibus_ref}
                        refreshKey={communicationsKey}
                        onUpload={isAdmin ? pickEmailFiles : undefined}
                        uploading={emailUploading}
                        onChanged={fetchOverview}
                        canManage={isAdmin}
                    />

                    <CollapsibleSection
                        title="Schedules"
                        count={`${formatCount(overview.schedules.length)} incremental`}
                        hint="The master sheet is the full live omnibus. Each numbered schedule is the incremental Excel that goes with an email to the lender."
                        defaultOpen={false}
                        actions={
                            isAdmin ? (
                                <button
                                    type="button"
                                    className={styles.secondaryBtn}
                                    onClick={buildMasterSchedule}
                                    disabled={buildingMaster || buildingNext || overview.stats.total_claims === 0}
                                >
                                    <FaFileExcel />
                                    {buildingMaster
                                        ? 'Building…'
                                        : overview.master_schedule
                                          ? 'Refresh master'
                                          : 'Build master'}
                                </button>
                            ) : undefined
                        }
                    >
                        <div className={styles.scheduleCards}>
                            <div className={styles.scheduleCard}>
                                <div className={styles.scheduleCardTop}>
                                    <div>
                                        <span className={styles.scheduleKind}>Master schedule</span>
                                        <span className={styles.stackSecondary}>Full omnibus · every active claim</span>
                                    </div>
                                    <span className={`${styles.statusPill} ${styles.open}`}>
                                        {overview.master_schedule ? 'Current' : 'Not built'}
                                    </span>
                                </div>
                                {overview.master_schedule ? (
                                    <>
                                        <div className={styles.scheduleMeta}>
                                            <span>{formatCount(overview.master_schedule.claim_count)} claims</span>
                                            <span>
                                                Generated{' '}
                                                {overview.master_schedule.generated_at
                                                    ? formatDate(overview.master_schedule.generated_at)
                                                    : '—'}
                                            </span>
                                            <span className={styles.fileName}>
                                                {overview.master_schedule.file_name || 'Excel sheet'}
                                            </span>
                                        </div>
                                        <div className={styles.scheduleActions}>
                                            <button
                                                type="button"
                                                className={styles.secondaryBtn}
                                                onClick={() => downloadSchedule('master')}
                                                disabled={downloadingKey === 'master'}
                                            >
                                                <FaDownload />
                                                {downloadingKey === 'master' ? 'Preparing…' : 'Download Excel'}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles.emptyStateSmall}>
                                        No master sheet yet. Build one to snapshot every claim currently in this
                                        omnibus.
                                    </div>
                                )}
                            </div>

                            {overview.schedules.length === 0 ? (
                                <div className={styles.emptyStateSmall}>
                                    No incremental schedules yet. Use Build schedule when you are ready to cut the first
                                    sheet for the lender email.
                                </div>
                            ) : (
                                overview.schedules.map((s) => (
                                    <div key={s.id} className={styles.scheduleCard}>
                                        <div className={styles.scheduleCardTop}>
                                            <div>
                                                <span className={styles.scheduleKind}>
                                                    Schedule #{s.schedule_number}
                                                </span>
                                                <span className={styles.stackSecondary}>
                                                    {s.sent_at
                                                        ? 'Sent snapshot · claims included in that lender email'
                                                        : 'Working sheet · new claims append here until marked sent'}
                                                </span>
                                            </div>
                                            <span className={`${styles.statusPill} ${styles[s.status] || styles.open}`}>
                                                {s.status}
                                            </span>
                                        </div>
                                        <div className={styles.scheduleMeta}>
                                            <span>{formatCount(s.claim_count)} claims</span>
                                            <span>Generated {s.generated_at ? formatDate(s.generated_at) : '—'}</span>
                                            {s.sent_at ? (
                                                <span>
                                                    Sent {formatDate(s.sent_at)}
                                                    {s.sent_by ? ` · ${s.sent_by}` : ''}
                                                </span>
                                            ) : (
                                                <span>Not marked as sent</span>
                                            )}
                                            {s.file_name && <span className={styles.fileName}>{s.file_name}</span>}
                                        </div>
                                        <div className={styles.scheduleActions}>
                                            {s.has_file && (
                                                <button
                                                    type="button"
                                                    className={styles.secondaryBtn}
                                                    onClick={() => downloadSchedule('incremental', s.id)}
                                                    disabled={downloadingKey === `schedule-${s.id}`}
                                                >
                                                    <FaDownload />
                                                    {downloadingKey === `schedule-${s.id}`
                                                        ? 'Preparing…'
                                                        : 'Download Excel'}
                                                </button>
                                            )}
                                            {isAdmin && !s.sent_at && (
                                                <button
                                                    type="button"
                                                    className={styles.actionBtnPrimary}
                                                    onClick={() => markScheduleSent(s.id)}
                                                    disabled={markingSentId === s.id}
                                                >
                                                    {markingSentId === s.id ? 'Saving…' : 'Mark as sent'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CollapsibleSection>

                    {overview.viewer_is_admin && (overview.by_introducer?.length ?? 0) > 0 && (
                        <CollapsibleSection
                            title="Source breakdown"
                            count={`${formatCount(overview.by_introducer!.length)} introducers`}
                            defaultOpen={false}
                        >
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Introducer</th>
                                            <th>Claims</th>
                                            <th>Clients</th>
                                            <th>Quantum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overview.by_introducer!.map((row) => (
                                            <tr key={row.introducer}>
                                                <td>{row.introducer}</td>
                                                <td>{formatCount(row.claim_count)}</td>
                                                <td>{formatCount(row.client_count)}</td>
                                                <td>{formatMoney(row.total_quantum)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CollapsibleSection>
                    )}

                    <CollapsibleSection
                        title="Claims & clients"
                        count={`${formatCount(total)} match${total === 1 ? '' : 'es'}`}
                        hint={
                            commsCoverage
                                ? `Client comms: ${formatCount(commsCoverage.claims_with_comms)} of ${formatCount(commsCoverage.total_claims)} claims have a recorded email/SMS history` +
                                  (commsCoverage.claims_without_comms > 0
                                      ? ` — ${formatCount(commsCoverage.claims_without_comms)} with nothing recorded (filter: "No comms recorded")`
                                      : '')
                                : undefined
                        }
                    >
                        <div className={styles.claimsHeader}>
                            <div className={styles.filters}>
                                <div className={styles.searchWrap}>
                                    <FaSearch className={styles.searchIcon} />
                                    <input
                                        className={styles.searchInput}
                                        type="search"
                                        placeholder="Search client, claim ref, agreement or policy number…"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                    />
                                </div>
                                <select
                                    className={styles.select}
                                    value={milestone}
                                    onChange={(e) => {
                                        setMilestone(e.target.value)
                                        setPage(1)
                                    }}
                                >
                                    <option value="">All milestones</option>
                                    {overview.milestones.map((m) => (
                                        <option key={m.key} value={m.key}>
                                            {m.label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className={styles.select}
                                    value={commsFilter}
                                    onChange={(e) => {
                                        setCommsFilter(e.target.value)
                                        setPage(1)
                                    }}
                                >
                                    <option value="">All comms</option>
                                    <option value="any">Has recorded comms</option>
                                    <option value="none">No comms recorded</option>
                                </select>
                                <select
                                    className={styles.select}
                                    value={sort}
                                    onChange={(e) => {
                                        setSort(e.target.value)
                                        setPage(1)
                                    }}
                                >
                                    {SORT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Claim</th>
                                        <th>Client</th>
                                        <th>Quantum</th>
                                        <th>Loan</th>
                                        <th>Start date</th>
                                        <th>ATE policy</th>
                                        <th>Milestone</th>
                                        {isAdmin && <th>Docs</th>}
                                        <th>Comms</th>
                                        <th aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {claims.length === 0 ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 10 : 9}>
                                                <div className={styles.emptyState}>
                                                    {claimsLoading
                                                        ? 'Loading…'
                                                        : search
                                                          ? 'No claims match that search.'
                                                          : 'No claims in this omnibus.'}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        claims.map((claim) => (
                                            <tr
                                                key={claim.id}
                                                className={styles.claimRow}
                                                onClick={() => openClaim(claim)}
                                            >
                                                <td>
                                                    <div className={styles.stackCell}>
                                                        <span className={styles.stackPrimary}>
                                                            {claim.funder_case_ref}
                                                        </span>
                                                        <span className={styles.stackSecondary}>
                                                            {claim.agreement_number || 'No agreement no.'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={styles.stackCell}>
                                                        <span className={styles.stackPrimary}>
                                                            {formatClientName(
                                                                claim.first_name,
                                                                claim.last_name,
                                                                String(claim.funder_client_id),
                                                            )}
                                                        </span>
                                                        <span className={styles.stackSecondary}>
                                                            {claim.email_address || '—'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {claim.has_detail ? (
                                                        formatMoney(claim.quantum_individual)
                                                    ) : (
                                                        <span className={styles.notCaptured}>Not captured</span>
                                                    )}
                                                </td>
                                                <td>{claim.has_detail ? formatMoney(claim.loan_value) : '—'}</td>
                                                <td>
                                                    {claim.has_detail ? formatDate(claim.agreement_start_date) : '—'}
                                                </td>
                                                <td>
                                                    <span className={styles.policyCell}>
                                                        {claim.ate_policy_number || '—'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${styles.milestonePill} ${
                                                            claim.milestone === 'm1_schedule_sent' ? styles.done : ''
                                                        }`}
                                                    >
                                                        {claim.milestone === 'm1_schedule_sent'
                                                            ? 'Schedule sent'
                                                            : 'Awaiting schedule'}
                                                    </span>
                                                </td>
                                                {isAdmin && (
                                                    <td>
                                                        {claim.has_detail ? (
                                                            <span className={styles.docPill}>
                                                                <FaPaperclip /> {claim.document_count}
                                                            </span>
                                                        ) : (
                                                            <span className={styles.pendingPill}>Not captured</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td>
                                                    {claim.comms_count > 0 ? (
                                                        <span className={styles.docPill}>
                                                            <FaEnvelope /> {claim.comms_count}
                                                        </span>
                                                    ) : (
                                                        <span className={styles.pendingPill}>None</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className={styles.viewBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            openClaim(claim)
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                    disabled={page <= 1 || claimsLoading}
                                >
                                    <FaChevronLeft /> Previous
                                </button>
                                <span className={styles.pageInfo}>
                                    Page {formatCount(page)} of {formatCount(totalPages)}
                                </span>
                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                    disabled={page >= totalPages || claimsLoading}
                                >
                                    Next <FaChevronRight />
                                </button>
                            </div>
                        )}
                    </CollapsibleSection>
                </>
            )}

            <ClaimDetailDrawer claim={detailClaim} onClose={() => setDetailClaim(null)} />
        </div>
    )
}
